import { VITE_CORE_API_URL } from "@/constants/env";
import type { StringMap } from "@/models/abstract-types";
import type {
	SubmissionTableParams,
	SubmissionTableSearchResult,
} from "@/models/tables/submission-table/submission-table.model";
import { auth } from "@/services/auth-service";

// Converts json to b64, used for sending small amounts of json
const jsonToBase64 = (json: string | StringMap | Record<string, unknown>) => {
	return btoa(encodeURIComponent(JSON.stringify(json)));
};

// Cache for organized submissions to avoid refetching on pagination
// Cache duration: 5 minutes for better performance
const CACHE_DURATION_MS = 5 * 60 * 1000;

let organizedCache: {
	data: any;
	timestamp: number;
	key: string;
} | null = null;

// Map of cache keys to fetch promises - allows multiple concurrent fetches for different queries
const fetchPromises = new Map<string, Promise<any>>();

// Function to clear the cache (useful for forcing fresh fetches after mutations)
// Call this after: creating, updating, or deleting submissions
export const clearOrganizedCache = () => {
	organizedCache = null;
	fetchPromises.clear();
};

const searchResponses = async (
	tableParams: SubmissionTableParams,
): Promise<SubmissionTableSearchResult> => {
	try {
		const {
			searchVal,
			selectedForm,
			selectedFormMode,
			pageNum,
			pageSize,
			sortBy,
			sortOrder,
			browserLocationId,
			filters,
			viewingDeleted,
		} = tableParams;

		// Convert old form mode values to new format for backward compatibility
		let normalizedFormMode = selectedFormMode;
		if (selectedFormMode === "0") normalizedFormMode = "submitted";
		if (selectedFormMode === "1") normalizedFormMode = "qa";
		if (selectedFormMode === "2") normalizedFormMode = "drafts";

		// If user is searching, use server-side search to find everything
		// Otherwise use cached organized endpoint for fast browsing
		const isSearching = searchVal && searchVal.trim().length > 0;

		if (isSearching) {
			// Use server-side search when user is actively searching
			// This allows finding submissions beyond the cached 200 limit
			const searchParams: Record<string, string> = {
				limit: pageSize.toString(),
				sort: sortBy,
				sort_direction: sortOrder,
				offset: ((pageNum - 1) * pageSize).toString(),
				include_deleted: String(viewingDeleted),
				search_value: searchVal.trim(),
			};

			// Add form_id filter
			if (selectedForm) {
				searchParams.form_id = String(selectedForm);
			}

			// Add location_id filter
			if (browserLocationId) {
				searchParams.location_id = browserLocationId;
			}

			// Add mode-specific filters
			if (normalizedFormMode === "qa") {
				searchParams.is_qa_flagged = "true";
			} else if (normalizedFormMode === "pending_approval") {
				searchParams.is_approved = "false";
			} else if (normalizedFormMode === "approved") {
				searchParams.is_approved = "true";
			} else if (normalizedFormMode === "drafts") {
				searchParams.form_mode = "2"; // Drafts mode
			} else if (normalizedFormMode === "submitted") {
				searchParams.form_mode = "0"; // Normal submitted mode
			}

			// Add column filters if present
			if (filters && Object.keys(filters).length > 0) {
				searchParams.column_filter = jsonToBase64(filters);
			}

			const params = new URLSearchParams(searchParams);
			const url = `${VITE_CORE_API_URL}/core/submission/search?${params.toString()}`;

			const response = await fetch(url, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
			});

			if (response.status !== 200) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(
					errorData.message || "Failed to search for submissions",
				);
			}

			const searchResult = await response.json();

			// Server-side search handles everything
			return {
				results: searchResult.data.hits || [],
				count: searchResult.data.total_hits || 0,
			};
		}

		// No search - use cached organized endpoint for fast browsing
		// Create cache key based on form and location (NOT including mode, pagination, or viewingDeleted)
		const cacheKey = `${selectedForm}_${browserLocationId || ""}`;

		let result;

		// Check if we have valid cached data first
		if (
			organizedCache &&
			organizedCache.key === cacheKey &&
			Date.now() - organizedCache.timestamp < CACHE_DURATION_MS &&
			selectedForm // Ensure form is set
		) {
			result = organizedCache.data;
		}

		// If no cached data, check if a fetch is already in progress for this cache key
		const existingPromise = fetchPromises.get(cacheKey);
		if (!result && existingPromise) {
			try {
				// Wait for the ongoing fetch to complete
				result = await existingPromise;
			} catch (error) {
				// If the ongoing fetch failed, we'll try again below
				console.warn("[Search] Ongoing fetch failed, will retry:", error);
			}
		}

		// Only fetch if we don't have cached data and no fetch is in progress for this key
		if (!result && !fetchPromises.has(cacheKey)) {
			// Create a new fetch promise for this cache key
			const newFetchPromise = (async () => {
				try {
					// Build URL with location_id query parameter if provided
					// Fetch ALL submissions (deleted + non-deleted) so we can filter client-side
					let organizedUrl = `${VITE_CORE_API_URL}/core/submission/organized`;
					if (browserLocationId) {
						organizedUrl += `?location_id=${browserLocationId}`;
					}

					const response = await fetch(organizedUrl, {
						method: "GET",
						headers: {
							Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
						},
					});

					if (response.status !== 200) {
						const errorData = await response.json().catch(() => ({}));
						throw new Error(
							errorData.message || "Failed to get organized submissions",
						);
					}

					const fetchedResult = await response.json();

					// Cache the result (all submissions, deleted + non-deleted)
					organizedCache = {
						data: fetchedResult,
						timestamp: Date.now(),
						key: cacheKey,
					};

					return fetchedResult;
				} finally {
					// Clear the promise when done (success or failure)
					fetchPromises.delete(cacheKey);
				}
			})();

			// Store the promise so other calls can wait for it
			fetchPromises.set(cacheKey, newFetchPromise);

			// Wait for our own fetch to complete
			result = await newFetchPromise;
		}

		let submissions = [];

		// Select the appropriate submission type based on form and mode
		// Form IDs 2, 3, and 4 all map to the main form submission arrays
		// (Org 28 uses form IDs 2 and 4, other orgs use form ID 3)
		if (
			selectedForm === "2" ||
			selectedForm === 2 ||
			selectedForm === "3" ||
			selectedForm === 3 ||
			selectedForm === "4" ||
			selectedForm === 4
		) {
			// Main form submissions (form IDs 2, 3, or 4)
			if (normalizedFormMode === "submitted") {
				submissions = result.data.submitted_submissions || [];
			} else if (normalizedFormMode === "qa") {
				submissions = result.data.qa_submissions || [];
			} else if (normalizedFormMode === "drafts") {
				submissions = result.data.draft_submissions || [];
			} else if (normalizedFormMode === "pending_approval") {
				// Org 28 only - pending approval submissions
				submissions = result.data.pending_approval_submissions || [];
			} else if (normalizedFormMode === "approved") {
				// Org 28 only - approved submissions
				submissions = result.data.approved_submissions || [];
			}
		} else if (selectedForm === "7" || selectedForm === 7) {
			// Property Condition Assessment form (form ID 7)
			if (normalizedFormMode === "submitted") {
				submissions = result.data.pca_submitted_submissions || [];
			} else if (normalizedFormMode === "qa") {
				submissions = result.data.pca_qa_submissions || [];
			} else if (normalizedFormMode === "drafts") {
				submissions = result.data.pca_draft_submissions || [];
			}
		}

		// Apply client-side filtering
		let filteredSubmissions = submissions;

		// Filter by form_id if needed (for org 28, form IDs 2 and 4 are in the same arrays)
		// Convert selectedForm to number for comparison
		const selectedFormId =
			typeof selectedForm === "string" ? parseInt(selectedForm) : selectedForm;
		if (selectedFormId && (selectedFormId === 2 || selectedFormId === 4)) {
			// For org 28, filter to only show submissions for the selected form ID
			filteredSubmissions = filteredSubmissions.filter(
				(submission: any) => submission.form_id === selectedFormId,
			);
		}

		// Apply search filter
		if (searchVal?.trim()) {
			const searchTerm = searchVal.trim().toLowerCase();
			filteredSubmissions = filteredSubmissions.filter(
				(submission: any) =>
					submission.submitting_user?.toLowerCase().includes(searchTerm) ||
					submission.location_name?.toLowerCase().includes(searchTerm) ||
					submission.asset_tag?.toLowerCase().includes(searchTerm) ||
					submission.asset_type_name?.toLowerCase().includes(searchTerm) ||
					submission.address?.toLowerCase().includes(searchTerm),
			);
		}

		// Apply deleted filter client-side (all submissions are already in cache)
		// Filter based on viewingDeleted state without refetching
		if (viewingDeleted) {
			// Show only deleted submissions
			filteredSubmissions = filteredSubmissions.filter(
				(submission: any) => submission.is_deleted === true,
			);
		} else {
			// Show only non-deleted submissions
			filteredSubmissions = filteredSubmissions.filter(
				(submission: any) => !submission.is_deleted,
			);
		}

		// Apply client-side sorting
		if (sortBy) {
			filteredSubmissions.sort((a: any, b: any) => {
				let aVal = a[sortBy];
				let bVal = b[sortBy];

				// Handle null/undefined values
				if (aVal == null && bVal == null) return 0;
				if (aVal == null) return 1;
				if (bVal == null) return -1;

				// Convert to strings for comparison
				const aStr = String(aVal).toLowerCase();
				const bStr = String(bVal).toLowerCase();

				const comparison = aStr.localeCompare(bStr);
				return sortOrder === "desc" ? -comparison : comparison;
			});
		}

		// Apply client-side pagination
		const startIndex = (pageNum - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		const paginatedSubmissions = filteredSubmissions.slice(
			startIndex,
			endIndex,
		);

		// Calculate count from filtered submissions (after all client-side filtering, before pagination)
		// This ensures the count reflects the actual number of submissions that match all filters
		const finalCount = filteredSubmissions.length;

		return {
			results: paginatedSubmissions,
			count: finalCount,
		};
	} catch (exception: unknown) {
		console.error(`Failed to search for submissions: ${exception}`);
		throw new Error("Failed to search for submissions");
	}
};

export { searchResponses };
